import { useParams, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Upload, Image, Check, Clock, AlertTriangle, XCircle, UserPlus, ShieldAlert, Camera, FileWarning, X, Copy, RefreshCw, WifiOff } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { checkRateLimit, recordUpload, getRateLimitStatus } from '@/lib/rateLimiter'
import { useDropByTokenQuery, useInsertPhotoMutation } from '@/data/contributor'
import { UPLOAD_WORKER_URL } from '@/lib/env'
import { resolveAssetUrl } from '@/lib/assetUrl'
import { tierMaxBytes, formatBytes as formatBytesTier } from '@/lib/tierLimits'

// Allowed image types (MIME types and extensions)
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif']
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif']

// File validation result
interface FileValidationResult {
  valid: boolean
  file: File
  error?: string
}

// Duplicate file detection
interface DuplicateFile {
  file: File
  hash: string
  existingName: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Simple hash function for file content (using first/last bytes + size for performance)
async function computeFileHash(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const buffer = e.target?.result as ArrayBuffer
      if (!buffer) {
        resolve(`${file.name}-${file.size}-${file.lastModified}`)
        return
      }

      // Create a simple hash from file content
      const bytes = new Uint8Array(buffer)
      let hash = 0
      const step = Math.max(1, Math.floor(bytes.length / 1000)) // Sample ~1000 bytes
      for (let i = 0; i < bytes.length; i += step) {
        hash = ((hash << 5) - hash + bytes[i]) | 0
      }
      resolve(`${hash.toString(16)}-${file.size}`)
    }
    reader.onerror = () => {
      resolve(`${file.name}-${file.size}-${file.lastModified}`)
    }
    reader.readAsArrayBuffer(file)
  })
}

// Validate a single file
function validateFile(file: File, maxBytes: number): FileValidationResult {
  if (file.size > maxBytes) {
    const sizeMB = Math.round(file.size / (1024 * 1024))
    return {
      valid: false,
      file,
      error: `File "${file.name}" is too large (${sizeMB}MB). Maximum size is ${formatBytesTier(maxBytes)}.`,
    }
  }

  // Check file type by MIME type
  const isValidMimeType = ALLOWED_MIME_TYPES.includes(file.type)

  // Also check by extension (some browsers don't set MIME type correctly for HEIC)
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  const isValidExtension = ALLOWED_EXTENSIONS.includes(extension)

  if (!isValidMimeType && !isValidExtension) {
    return {
      valid: false,
      file,
      error: `File "${file.name}" is not a supported image format. Please upload JPEG, PNG, WebP, or HEIC files.`,
    }
  }

  return { valid: true, file }
}

// Validate image can actually be read (detect corrupted files)
async function validateImageReadable(file: File): Promise<FileValidationResult> {
  return new Promise((resolve) => {
    // For HEIC files, we can't easily validate in browser - accept them
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (extension === 'heic' || extension === 'heif') {
      resolve({ valid: true, file })
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const img = document.createElement('img')
      img.onload = () => {
        resolve({ valid: true, file })
      }
      img.onerror = () => {
        resolve({
          valid: false,
          file,
          error: `File "${file.name}" appears to be corrupted and cannot be read.`,
        })
      }
      img.src = e.target?.result as string
    }
    reader.onerror = () => {
      resolve({
        valid: false,
        file,
        error: `File "${file.name}" could not be read. The file may be corrupted.`,
      })
    }
    reader.readAsDataURL(file)
  })
}

// Create a thumbnail from an image file (max 300x300)
async function createThumbnail(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = document.createElement('img')
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxSize = 300
        let width = img.width
        let height = img.height

        // Scale down to fit within maxSize while maintaining aspect ratio
        if (width > height) {
          if (width > maxSize) {
            height = (height / width) * maxSize
            width = maxSize
          }
        } else {
          if (height > maxSize) {
            width = (width / height) * maxSize
            height = maxSize
          }
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0, width, height)

        resolve(canvas.toDataURL('image/jpeg', 0.8))
      }
      img.onerror = () => {
        // If image fails to load, use a placeholder
        reject(new Error('Failed to create thumbnail'))
      }
      img.src = e.target?.result as string
    }
    reader.onerror = () => {
      reject(new Error('Failed to read file for thumbnail'))
    }
    reader.readAsDataURL(file)
  })
}

// Drop status types for UI
type DropUIStatus = 'loading' | 'active' | 'closed' | 'expired' | 'invalid' | 'error'

interface DropBranding {
  primaryColor?: string
  accentColor?: string
  logoUrl?: string
  headerText?: string
}

interface DropInfo {
  id: string
  name: string
  description?: string
  coverPhotoUrl?: string
  status: DropUIStatus
  expiresAt?: Date
  closedReason?: string
  customThankYouMessage?: string
  orgTier?: 'free' | 'pro' | 'business'
  organizationId?: string
  branding?: DropBranding
  contributorId?: string
}

// Uploaded photo for display in the "My Uploads" grid
interface UploadedPhoto {
  id: string
  originalUrl: string
  thumbnailUrl?: string
  filename: string
  caption?: string
}

export default function ContributorPage() {
  const { token } = useParams()
  const [dropInfo, setDropInfo] = useState<DropInfo | null>(null)
  const [uploaded, setUploaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [rateLimitError, setRateLimitError] = useState<string | null>(null)
  const [rateLimitStatus, setRateLimitStatus] = useState(getRateLimitStatus())
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [isValidating, setIsValidating] = useState(false)
  const [uploadedHashes, setUploadedHashes] = useState<Map<string, string>>(new Map()) // hash -> filename
  const [duplicateFiles, setDuplicateFiles] = useState<DuplicateFile[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([]) // Files waiting for duplicate resolution
  const [myPhotos, setMyPhotos] = useState<UploadedPhoto[]>([]) // Photos uploaded by this contributor
  const [caption, setCaption] = useState('') // Caption for photos
  const [uploadError, setUploadError] = useState<string | null>(null) // Upload error message
  const [failedFiles, setFailedFiles] = useState<File[]>([]) // Files that failed to upload
  const [isRetrying, setIsRetrying] = useState(false) // Whether we're retrying failed uploads
  const [isOnline, setIsOnline] = useState(navigator.onLine) // Network status
  const [uploadSummary, setUploadSummary] = useState<{ fileCount: number; totalBytes: number } | null>(null)
  const [contributorEnvelope, setContributorEnvelope] = useState<string | null>(null)
  // Fetch drop info through the contributor worker.
  const { contributor, isLoading: isDropLoading, error: dropQueryError } = useDropByTokenQuery(token)

  // Keep the short-lived contributor session token returned by the worker.
  useEffect(() => {
    setContributorEnvelope(contributor?.sessionToken ?? null)
  }, [contributor])

  // Photo insert mutation
  const insertPhoto = useInsertPhotoMutation()

  // Map contributor worker response to DropInfo.
  useEffect(() => {
    if (isDropLoading) return

    if (dropQueryError) {
      setError('Failed to load drop information')
      setDropInfo({ id: '', name: '', status: 'error' })
      return
    }

    if (!token) {
      setDropInfo({ id: '', name: '', status: 'invalid' })
      return
    }

    if (!contributor) {
      setDropInfo({ id: '', name: '', status: 'invalid' })
      return
    }

    const drop = contributor.drop

    // Map drop status to UI status
    let uiStatus: DropUIStatus = 'active'
    if (drop.status === 'closed') {
      uiStatus = 'closed'
    } else if (drop.status === 'expired') {
      uiStatus = 'expired'
    }
    const closesAt = drop.closes_at ?? drop.closesAt
    if (closesAt && new Date() > new Date(closesAt)) {
      uiStatus = 'expired'
    }

    const branding = (drop.organization?.branding_config ?? drop.organization?.brandingConfig) as DropBranding | null

    setDropInfo({
      id: drop.id,
      name: drop.name,
      description: drop.description || undefined,
      coverPhotoUrl: resolveAssetUrl(drop.cover_photo_url ?? drop.coverPhotoUrl),
      status: uiStatus,
      expiresAt: closesAt ? new Date(closesAt) : undefined,
      closedReason: drop.status === 'closed'
        ? 'The organizer has closed this drop.'
        : drop.status === 'expired'
        ? 'The submission deadline has passed.'
        : undefined,
      orgTier: (drop.organization?.tier as 'free' | 'pro' | 'business') || 'free',
      organizationId: drop.organization?.id || '',
      branding: branding || undefined,
      contributorId: contributor.id,
    })
  }, [contributor, isDropLoading, dropQueryError, token])

  // Listen for network status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (uploadError && uploadError.includes('network')) {
        setUploadError(null)
      }
    }
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [uploadError])

  // Core upload function: the worker creates the photo row, signs R2 upload,
  // verifies completion, and triggers processing.
  const performUpload = useCallback(async (filesToUpload: File[], isRetry = false) => {
    if (filesToUpload.length === 0) return

    if (!UPLOAD_WORKER_URL) {
      setUploadError('Upload service is not configured. Please contact the organizer.')
      return
    }

    if (!dropInfo?.id || !dropInfo?.contributorId) {
      setUploadError('Drop information is missing. Please refresh the page.')
      return
    }

    if (!contributorEnvelope) {
      setUploadError('Access verification is still loading. Please wait a moment and try again.')
      return
    }

    // Check if offline
    if (!navigator.onLine) {
      setUploadError('You appear to be offline. Please check your network connection and try again.')
      setFailedFiles(filesToUpload)
      return
    }

    // Check rate limit before upload
    const rateCheck = checkRateLimit()
    if (!rateCheck.allowed) {
      setRateLimitError(rateCheck.message || 'Upload limit reached. Please wait.')
      setRateLimitStatus(getRateLimitStatus())
      return
    }

    // Clear previous error when starting upload
    setUploadError(null)
    if (isRetry) {
      setIsRetrying(true)
    }

    // Process and upload files
    setUploadProgress(0)
    const captionValue = caption.trim()
    const failedUploads: File[] = []
    let successCount = 0

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i]

      // Check network status before each file
      if (!navigator.onLine) {
        failedUploads.push(...filesToUpload.slice(i))
        setUploadError('Network connection lost during upload. Please retry when connection is restored.')
        break
      }

      try {
        const contentType = file.type || 'application/octet-stream'
        const fileHash = await computeFileHash(file)
        const uploadResult = await insertPhoto.mutateAsync({
          file,
          sessionToken: contributorEnvelope,
          contentType,
          caption: captionValue || undefined,
          fileHash,
        })
        const originalUrl = resolveAssetUrl(uploadResult.originalUrl) ?? ''

        // Create thumbnail for immediate display (stays as data URL).
        let thumbnailDataUrl: string | undefined
        try {
          thumbnailDataUrl = await createThumbnail(file)
        } catch {
          // Thumbnail generation can fail for HEIC etc, not critical
        }

        setMyPhotos(prev => [...prev, {
          id: uploadResult.photoId,
          originalUrl,
          thumbnailUrl: thumbnailDataUrl,
          filename: file.name,
          caption: captionValue || undefined,
        }])

        successCount++
      } catch (err) {
        console.error(`Failed to upload file ${file.name}:`, err)
        failedUploads.push(file)
      }

      // Update progress
      const progress = Math.round(((i + 1) / filesToUpload.length) * 100)
      setUploadProgress(progress)
    }

    // Handle failed uploads
    if (failedUploads.length > 0) {
      setFailedFiles(failedUploads)
      if (!uploadError) {
        setUploadError(`${failedUploads.length} file${failedUploads.length > 1 ? 's' : ''} failed to upload. Please retry.`)
      }
    } else {
      setFailedFiles([])
    }

    // Record upload for rate limiting
    if (successCount > 0) {
      recordUpload(successCount)
      setRateLimitStatus(getRateLimitStatus())
    }

    // Record file hashes for duplicate detection
    const newHashes = new Map(uploadedHashes)
    for (const file of filesToUpload.filter(f => !failedUploads.includes(f))) {
      const hash = await computeFileHash(file)
      newHashes.set(hash, file.name)
    }
    setUploadedHashes(newHashes)

    setUploadProgress(null)
    setIsRetrying(false)

    // Only clear selected files and show success if all uploads succeeded
    if (failedUploads.length === 0) {
      setUploadSummary({
        fileCount: filesToUpload.length,
        totalBytes: filesToUpload.reduce((sum, f) => sum + f.size, 0),
      })
      setSelectedFiles([])
      setCaption('') // Clear caption
      setUploaded(true)
    } else {
      // Remove successfully uploaded files from selection
      setSelectedFiles(failedUploads)
    }
  }, [caption, dropInfo, uploadedHashes, uploadError, insertPhoto])

  // Retry failed uploads
  const handleRetryUpload = useCallback(() => {
    if (failedFiles.length > 0) {
      performUpload(failedFiles, true)
    }
  }, [failedFiles, performUpload])

  // Handle file selection with validation
  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (files.length === 0) return

    setIsValidating(true)
    setFileErrors([])
    setDuplicateFiles([])

    const validFiles: File[] = []
    const errors: string[] = []
    const duplicates: DuplicateFile[] = []

    const orgMaxBytes = tierMaxBytes(dropInfo?.orgTier)

    for (const file of files) {
      // First, basic validation (size and type)
      const basicResult = validateFile(file, orgMaxBytes)
      if (!basicResult.valid) {
        errors.push(basicResult.error!)
        continue
      }

      // Then, validate the image is readable (not corrupted)
      const readableResult = await validateImageReadable(file)
      if (!readableResult.valid) {
        errors.push(readableResult.error!)
        continue
      }

      // Check for duplicates against already uploaded files
      const hash = await computeFileHash(file)
      const existingFile = uploadedHashes.get(hash)
      if (existingFile) {
        duplicates.push({
          file,
          hash,
          existingName: existingFile,
        })
        continue
      }

      // Also check against currently selected files
      const isAlreadySelected = selectedFiles.some(async (sf) => {
        const sfHash = await computeFileHash(sf)
        return sfHash === hash
      })
      if (isAlreadySelected) {
        errors.push(`File "${file.name}" is already in your selection.`)
        continue
      }

      validFiles.push(file)
    }

    if (errors.length > 0) {
      setFileErrors(errors)
    }

    if (duplicates.length > 0) {
      setDuplicateFiles(duplicates)
      setPendingFiles(validFiles) // Store non-duplicate files for later
    } else if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
    }

    setIsValidating(false)
  }, [uploadedHashes, selectedFiles])

  // Handle skipping duplicate files
  const handleSkipDuplicates = useCallback(() => {
    // Add pending (non-duplicate) files to selection
    if (pendingFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...pendingFiles])
    }
    setDuplicateFiles([])
    setPendingFiles([])
  }, [pendingFiles])

  // Handle replacing duplicate files
  const handleReplaceDuplicates = useCallback(async () => {
    // Remove the original files and add the new ones
    const newHashes = new Map(uploadedHashes)
    const filesToAdd: File[] = [...pendingFiles]

    for (const dup of duplicateFiles) {
      // Update the hash to point to the new file
      newHashes.set(dup.hash, dup.file.name)
      filesToAdd.push(dup.file)
    }

    setUploadedHashes(newHashes)
    setSelectedFiles((prev) => [...prev, ...filesToAdd])
    setDuplicateFiles([])
    setPendingFiles([])
  }, [duplicateFiles, pendingFiles, uploadedHashes])

  // Check for expiry periodically
  useEffect(() => {
    if (dropInfo?.status !== 'active' || !dropInfo.expiresAt) return

    const checkExpiry = () => {
      if (dropInfo.expiresAt && new Date() > dropInfo.expiresAt) {
        setDropInfo(prev => prev ? {
          ...prev,
          status: 'expired',
          closedReason: 'The submission deadline has passed.'
        } : null)
      }
    }

    // Check every second
    const interval = setInterval(checkExpiry, 1000)
    return () => clearInterval(interval)
  }, [dropInfo])

  // Update rate limit status periodically
  useEffect(() => {
    const updateRateLimitStatus = () => {
      const status = getRateLimitStatus()
      setRateLimitStatus(status)
      // Clear error message if no longer rate limited
      if (!status.isLimited && rateLimitError) {
        setRateLimitError(null)
      }
    }

    // Update every second when rate limited
    const interval = setInterval(updateRateLimitStatus, 1000)
    return () => clearInterval(interval)
  }, [rateLimitError])

  // Loading state
  if (!dropInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drop...</p>
        </div>
      </div>
    )
  }

  // Invalid token
  if (dropInfo.status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-lg border shadow-sm p-8 text-center">
          <div className="h-16 w-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="h-8 w-8 text-destructive" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Invalid Link</h1>
          <p className="mt-4 text-muted-foreground">
            This invite link is invalid or has been revoked.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Please contact the event organizer for a new link.
          </p>
        </div>
      </div>
    )
  }

  // Error state
  if (dropInfo.status === 'error' || error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-lg border shadow-sm p-8 text-center">
          <div className="h-16 w-16 bg-accent/15 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-accent" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Something Went Wrong</h1>
          <p className="mt-4 text-muted-foreground">
            {error || 'We could not load this drop. Please try again later.'}
          </p>
          <Button className="mt-6" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  // Expired state
  if (dropInfo.status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-lg border shadow-sm p-8 text-center">
          <div className="h-16 w-16 bg-accent/15 rounded-full flex items-center justify-center mx-auto">
            <Clock className="h-8 w-8 text-accent" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Drop Expired</h1>
          <p className="mt-4 text-muted-foreground">
            {dropInfo.closedReason || 'This photo drop is no longer accepting uploads.'}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            The submission deadline for "{dropInfo.name}" has passed.
          </p>
        </div>
      </div>
    )
  }

  // Closed state
  if (dropInfo.status === 'closed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-lg border shadow-sm p-8 text-center">
          <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <XCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Drop Closed</h1>
          <p className="mt-4 text-muted-foreground">
            {dropInfo.closedReason || 'This photo drop is no longer accepting uploads.'}
          </p>
        </div>
      </div>
    )
  }

  // Success state after upload
  if (uploaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="max-w-md w-full bg-card text-card-foreground rounded-lg border shadow-sm p-8 text-center">
          <div className="h-16 w-16 bg-secondary/15 rounded-full flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-secondary" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Thank You!</h1>
          <p className="mt-4 text-muted-foreground">
            {dropInfo?.customThankYouMessage || 'Your photos have been uploaded successfully.'}
          </p>
          {uploadSummary && (
            <p className="mt-2 text-sm text-muted-foreground">
              {uploadSummary.fileCount} photo{uploadSummary.fileCount !== 1 ? 's' : ''} uploaded ({formatBytes(uploadSummary.totalBytes)})
            </p>
          )}
          <div className="mt-6 space-y-3">
            <Button className="w-full" onClick={() => setUploaded(false)}>
              Upload More Photos
            </Button>

            {/* Optional account creation */}
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground mb-3">
                Want to track your uploads and access them later?
              </p>
              <Button
                variant="outline"
                className="w-full"
                asChild
              >
                <Link to={`/signup?contributor=${token}&drop=${dropInfo?.id}`}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create an Account
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Active drop - upload interface
  // Get custom branding colors
  const primaryColor = dropInfo.branding?.primaryColor
  const accentColor = dropInfo.branding?.accentColor

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b p-4" style={primaryColor ? { borderBottomColor: primaryColor } : {}}>
        <div className="max-w-2xl mx-auto text-center">
          {/* Custom Logo */}
          {dropInfo.branding?.logoUrl && (
            <div className="mb-4">
              <img
                src={dropInfo.branding.logoUrl}
                alt="Event logo"
                className="h-16 max-w-[250px] object-contain mx-auto"
                onError={(e) => {
                  // Hide if logo fails to load
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            </div>
          )}
          <h1 className="text-xl font-bold">{dropInfo.name}</h1>
          {dropInfo.description && (
            <p className="text-muted-foreground mt-1">{dropInfo.description}</p>
          )}
          {dropInfo.expiresAt && (
            <ExpiryCountdown expiresAt={dropInfo.expiresAt} />
          )}
        </div>
      </header>

      {/* Upload Area */}
      <main className="max-w-2xl mx-auto p-4">
        <div className="bg-card text-card-foreground rounded-lg border shadow-sm p-8">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer ${
              isDragging
                ? 'border-primary bg-primary/5 scale-[1.02]'
                : 'hover:border-primary'
            }`}
            style={primaryColor ? {
              borderColor: isDragging ? primaryColor : undefined,
              backgroundColor: isDragging ? `${primaryColor}10` : undefined,
            } : {}}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragging(true)
            }}
            onDragEnter={(e) => {
              e.preventDefault()
              setIsDragging(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              setIsDragging(false)
            }}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragging(false)
              // Pass all files to validation - it will filter and report errors
              const files = Array.from(e.dataTransfer.files)
              handleFilesSelected(files)
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            {/* File input for gallery/file selection */}
            <input
              id="file-input"
              type="file"
              accept="image/*,image/heic,image/heif,.heic,.heif"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                handleFilesSelected(files)
                // Reset input so same file can be selected again
                e.target.value = ''
              }}
            />
            {/* Camera capture input for mobile devices */}
            <input
              id="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || [])
                handleFilesSelected(files)
                // Reset input so same file can be selected again
                e.target.value = ''
              }}
            />
            <Upload
              className={`h-12 w-12 mx-auto ${isDragging ? 'text-primary' : 'text-muted-foreground'}`}
              style={primaryColor && isDragging ? { color: primaryColor } : {}}
            />
            <h3 className="mt-4 font-semibold">
              {isDragging ? 'Drop your photos here!' : 'Drop your photos here'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              or click to browse files
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Supports JPEG, PNG, WebP, HEIC (max {formatBytesTier(tierMaxBytes(dropInfo?.orgTier))} each)
            </p>

            {/* Mobile camera capture button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                document.getElementById('camera-input')?.click()
              }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors"
              style={primaryColor ? { color: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
            >
              <Camera className="h-4 w-4" />
              Take Photo
            </button>
          </div>

          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="relative group bg-muted rounded-md p-2 text-xs flex items-center gap-2"
                  >
                    <Image className="h-4 w-4" />
                    <span className="max-w-[100px] truncate">{file.name}</span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress !== null && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%`, ...(accentColor ? { backgroundColor: accentColor } : {}) }}
                />
              </div>
            </div>
          )}

          {/* Caption input */}
          <div className="mt-6">
            <label htmlFor="caption" className="block text-sm font-medium mb-2">
              Add a caption (optional)
            </label>
            <input
              id="caption"
              type="text"
              placeholder="Describe your photo..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* File validation errors */}
          {fileErrors.length > 0 && (
            <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FileWarning className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-destructive">
                      {fileErrors.length === 1 ? 'File could not be added' : `${fileErrors.length} files could not be added`}
                    </p>
                    <button
                      type="button"
                      onClick={() => setFileErrors([])}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Dismiss errors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {fileErrors.map((error, index) => (
                      <li key={index} className="text-sm text-destructive">
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Validating indicator */}
          {isValidating && (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span>Checking files...</span>
            </div>
          )}

          {/* Duplicate file warning */}
          {duplicateFiles.length > 0 && (
            <div className="mt-4 bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Copy className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    {duplicateFiles.length === 1 ? 'Duplicate photo detected' : `${duplicateFiles.length} duplicate photos detected`}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {duplicateFiles.map((dup, index) => (
                      <li key={index} className="text-sm text-muted-foreground">
                        • "{dup.file.name}" matches previously uploaded "{dup.existingName}"
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSkipDuplicates}
                      className="border-border text-foreground hover:bg-muted"
                    >
                      Skip Duplicates
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleReplaceDuplicates}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Replace Original{duplicateFiles.length > 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rate limit warning */}
          {rateLimitError && (
            <div className="mt-4 bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-accent flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Upload limit reached</p>
                  <p className="text-sm text-muted-foreground mt-1">{rateLimitError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Rate limit indicator */}
          {rateLimitStatus.uploadsUsed > 0 && !rateLimitError && (
            <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Uploads: {rateLimitStatus.uploadsUsed}/{rateLimitStatus.uploadsLimit} per minute
              </span>
              {rateLimitStatus.resetIn && (
                <span>Resets in {rateLimitStatus.resetIn}s</span>
              )}
            </div>
          )}

          {/* Upload error with retry button */}
          {uploadError && (
            <div className="mt-4 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                {!isOnline ? (
                  <WifiOff className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">Upload Failed</p>
                  <p className="text-sm text-destructive mt-1">{uploadError}</p>
                  {failedFiles.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {failedFiles.length} file{failedFiles.length > 1 ? 's' : ''} failed: {failedFiles.map(f => f.name).join(', ')}
                    </p>
                  )}
                  <div className="mt-3 flex gap-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleRetryUpload}
                      disabled={!isOnline || isRetrying}
                      className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${isRetrying ? 'animate-spin' : ''}`} />
                      {isRetrying ? 'Retrying...' : 'Retry Upload'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUploadError(null)
                        setFailedFiles([])
                      }}
                      className="border-border text-foreground hover:bg-muted"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Offline indicator */}
          {!isOnline && !uploadError && (
            <div className="mt-4 bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <WifiOff className="h-5 w-5 text-accent" />
                <p className="text-sm text-muted-foreground">
                  You're offline. Upload will be available when connection is restored.
                </p>
              </div>
            </div>
          )}

          {/* Upload button */}
          <Button
            className="w-full mt-6"
            size="lg"
            disabled={selectedFiles.length === 0 || uploadProgress !== null || rateLimitStatus.isLimited || isValidating || !isOnline}
            onClick={() => performUpload(selectedFiles)}
            style={primaryColor ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploadProgress !== null
              ? 'Uploading...'
              : selectedFiles.length > 0
              ? `Upload ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`
              : 'Upload Photos'}
          </Button>
        </div>

        {/* My Uploads */}
        <div className="mt-8">
          <h3 className="font-semibold mb-4">My Uploads ({myPhotos.length})</h3>
          {myPhotos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {myPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative bg-card rounded-lg border overflow-hidden"
                >
                  <div className="aspect-square">
                    <img
                      src={photo.thumbnailUrl || photo.originalUrl}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {photo.caption && (
                    <div className="p-2 text-xs text-muted-foreground truncate">
                      {photo.caption}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-card text-card-foreground rounded-lg border p-8 text-center">
              <Image className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="mt-2 text-sm text-muted-foreground">
                No uploads yet
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-8 p-4 text-center text-sm text-muted-foreground">
        {dropInfo.branding?.logoUrl ? (
          <div className="flex flex-col items-center gap-2">
            <img
              src={dropInfo.branding.logoUrl}
              alt="Event logo"
              className="h-10 max-w-[200px] object-contain"
              onError={(e) => {
                // Fallback to text if logo fails to load
                (e.target as HTMLImageElement).style.display = 'none'
                const fallback = document.createElement('span')
                fallback.textContent = 'Powered by Photodrop'
                e.currentTarget.parentElement?.appendChild(fallback)
              }}
            />
          </div>
        ) : (
          <span>Powered by Photodrop</span>
        )}
      </footer>
    </div>
  )
}

// Countdown component showing time until drop expires
function ExpiryCountdown({ expiresAt }: { expiresAt: Date }) {
  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date()
      const diff = expiresAt.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeLeft('Expired')
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      if (hours > 24) {
        const days = Math.floor(hours / 24)
        setTimeLeft(`${days} day${days > 1 ? 's' : ''} left`)
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m left`)
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s left`)
      } else {
        setTimeLeft(`${seconds}s left`)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [expiresAt])

  return (
    <p className="mt-2 text-sm text-orange-600 flex items-center justify-center gap-1">
      <Clock className="h-4 w-4" />
      {timeLeft}
    </p>
  )
}
